import { useEffect, useState } from "react";
import { FormModal, Input, Select } from "@marekl/adminer/components";
import {
  seatLabel,
  useAssignSeat,
  useJudges,
  type PanelSeat,
} from "../../../lib/queries/panels";
import type { ID } from "../../../lib/id";

interface SeatModalProps {
  competitionId: ID;
  running: boolean;
  /** The panel the seat belongs to; null for the head judge's seat. */
  panelId: ID | null;
  /** The seat being filled, or null when the dialog is closed. */
  seat: PanelSeat | null;
  onClose: () => void;
}

// Who sits on one seat: a judge from the federation's list (a user with the
// Porotce role), or for a judge outside it (a pohárovka) just a name. One or
// the other; both empty frees the seat.
export function SeatModal({
  competitionId,
  running,
  panelId,
  seat,
  onClose,
}: SeatModalProps) {
  const assign = useAssignSeat(competitionId, running);
  // Only the judges who may sit here: not on another seat of this panel,
  // not on a panel of another lane of the same group, not the head judge
  // on a panel (and the other way round).
  const { data: judges = [], isLoading } = useJudges(
    competitionId,
    seat ? { panel: panelId, kind: seat.kind, position: seat.position } : null,
  );
  const [user, setUser] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (seat) {
      setUser(seat.user_id ?? "");
      setName(seat.judge_name ?? "");
      assign.reset();
    }
    // Reset when another seat is opened, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- assign is a fresh object each render
  }, [seat]);

  // A holder who is no longer on offer (role taken away, account closed)
  // is not shown by the select, so must not be sent back either.
  useEffect(() => {
    if (!isLoading && user !== "" && !judges.some((j) => j.id === user))
      setUser("");
  }, [isLoading, judges, user]);

  if (!seat) return null;
  const both = user !== "" && name.trim() !== "";

  return (
    <FormModal
      open
      title={seatLabel(seat)}
      error={assign.error}
      busy={assign.isPending}
      canSubmit={!both}
      onSubmit={() =>
        assign.mutate(
          {
            panel: panelId,
            kind: seat.kind,
            position: seat.position,
            user: user === "" ? null : user,
            name: user === "" ? name.trim() : "",
          },
          { onSuccess: onClose },
        )
      }
      onClose={onClose}
    >
      <Select
        label="Porotce"
        value={user}
        loading={isLoading}
        hint={
          !isLoading && judges.length === 0
            ? "Sem nemůže žádný porotce ze seznamu. Napište jméno, nebo nejdřív uvolněte jiné místo."
            : "Jen porotci, kteří sem podle pravidel smějí."
        }
        onChange={(e) => setUser(e.target.value)}
      >
        <option value="">Neobsazeno / jiné jméno</option>
        {judges.map((j) => (
          <option key={j.id} value={j.id}>
            {j.name}
          </option>
        ))}
      </Select>
      <Input
        label="Nebo jméno porotce mimo federaci"
        value={name}
        disabled={user !== ""}
        hint="Pro porotce bez účtu, třeba na pohárovce."
        error={
          both
            ? "Vyberte porotce ze seznamu, nebo napište jméno; ne obojí."
            : undefined
        }
        onChange={(e) => setName(e.target.value)}
      />
    </FormModal>
  );
}
